import React, { useEffect, useState, useCallback } from 'react';
import _ from 'lodash';
import {
  ThreeStepsDetails,
  EndOfLifeCareDetails,
  FileListData,
} from '@typings/ResidentTypes';
import useResidentDetailsFetch from '@hooks/useResidentDetailsFetch';
import { ErrorsProvider } from '@contexts/ErrorsContext';
import { FormError, SubmitStatus, SubmitStatusEnum } from '@typings/common';
import SuccessNotificationBanner from '@components/SuccessNotificationBanner';
import PrognosisSection from '@components/resident-notes/prognosis-section';
import SummaryCoreWishesSection from './components/SummaryCoreWishesSection';
import PreferredPlacesSection from './components/PreferredPlacesSection';
import RespectFormSection from './components/RespectFormSection';
import {
  preferredPlaceOfCareOptions,
  preferredPlaceOfDeathOptions,
} from './components/commons';
import type { PreferredPlaceOption } from './components/commons';
import {
  ShowSuccessNotificationType,
  SuccessNotiPositionEnum,
} from '../commons';
import SaveAndContinueButton from './components/SaveAndContinueButton';
import UniversalCarePlanSection from './components/UniversalCarePlanSection';
import PalliativeCareRegisterSection from './components/PalliativeCareRegisterSection';

enum StepChangeDirection {
  Previous = 'prev',
  Next = 'next',
}

interface EndOfLifeCareForm {
  summaryCoreWishes: string;
  preferredPlaceOfCareFirstChoice: string;
  preferredPlaceOfCareSecondChoice: string;
  preferredPlaceOfDeathFirstChoice: string;
  preferredPlaceOfDeathSecondChoice: string;
  hasRespectFormCompleted: string;
  respectFormFiles: FileListData[];
  hasUniversalCarePlan: string;
  isOnPalliativeCareRegister: string;
  familyAwareOfPrognosis: string;
}

interface StepFourProps {
  role: string;
  slug: string | undefined;
  userId: string;
  threeStepsData: ThreeStepsDetails;
  changeStep: (status: StepChangeDirection) => void;
  callApiStatus: SubmitStatus;
  showSuccessNotification: ShowSuccessNotificationType;
  handleGetResidentDetails: () => Promise<void>;
  onSubmitThreeStepsData: (
    submitValues: ThreeStepsDetails,
    position: SuccessNotiPositionEnum
  ) => void;
}

export interface NextOfKinDetails {
  fullName: string;
  relationship: string;
  phoneNumber: string;
}

const initialEndOfLifeCare: EndOfLifeCareForm = {
  summaryCoreWishes: '',

  preferredPlaceOfCareFirstChoice: '',
  preferredPlaceOfCareSecondChoice: '',
  preferredPlaceOfDeathFirstChoice: '',
  preferredPlaceOfDeathSecondChoice: '',

  hasRespectFormCompleted: '',
  respectFormFiles: [],
  hasUniversalCarePlan: '',
  isOnPalliativeCareRegister: '',
  familyAwareOfPrognosis: '',
};

const StepFourComponent: React.FC<StepFourProps> = ({
  role,
  slug,
  userId,
  threeStepsData,
  changeStep,
  callApiStatus,
  showSuccessNotification,
  handleGetResidentDetails,
  onSubmitThreeStepsData,
}: StepFourProps) => {
  const { residentDetails } = useResidentDetailsFetch(slug);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isUpdatedPronosis, setIsUpdatedPrognosis] = useState(false);
  const [endOfLifeCare, setEndOfLifeCare] =
    useState<EndOfLifeCareForm>(initialEndOfLifeCare);

  const residentFullName = residentDetails?.profile.full_name ?? '';
  const prognosisFromData = residentDetails?.profile.prognosis ?? '';

  const updateEndOfLifeCare = useCallback(
    <K extends keyof EndOfLifeCareForm>(
      field: K,
      value: EndOfLifeCareForm[K]
    ) => {
      setEndOfLifeCare(prev => ({
        ...prev,
        [field]: value,
      }));
    },
    []
  );

  useEffect(() => {
    handleGetResidentDetails();
  }, [handleGetResidentDetails]);

  useEffect(() => {
    if (!threeStepsData?.step4) return;

    const step4Data = threeStepsData.step4;

    setEndOfLifeCare({
      summaryCoreWishes: step4Data.summaryCoreWishes ?? '',

      preferredPlaceOfCareFirstChoice:
        step4Data.preferredPlaceOfCareFirstChoice?.value ?? '',

      preferredPlaceOfCareSecondChoice:
        step4Data.preferredPlaceOfCareSecondChoice?.value ?? '',

      preferredPlaceOfDeathFirstChoice:
        step4Data.preferredPlaceOfDeathFirstChoice?.value ?? '',

      preferredPlaceOfDeathSecondChoice:
        step4Data.preferredPlaceOfDeathSecondChoice?.value ?? '',

      hasRespectFormCompleted: step4Data.hasRespectFormCompleted ?? '',

      respectFormFiles: step4Data.respectFormFiles ?? [],

      hasUniversalCarePlan: step4Data.hasUniversalCarePlan ?? '',

      isOnPalliativeCareRegister: step4Data.isOnPalliativeCareRegister ?? '',

      familyAwareOfPrognosis: step4Data.familyAwareOfPrognosis ?? '',
    });
  }, [threeStepsData?.step4]);

  const getSelectedPreferredPlaceOption = (
    options: PreferredPlaceOption[],
    value: string
  ): PreferredPlaceOption | null => {
    if (!value) return null;

    return options.find(option => option.value === value) ?? null;
  };

  const handleSaveEndOfLifeCare = async (
    e: React.MouseEvent<HTMLButtonElement>,
    position: SuccessNotiPositionEnum
  ) => {
    e.preventDefault();
    const payload: EndOfLifeCareDetails = {
      summaryCoreWishes: endOfLifeCare.summaryCoreWishes,

      preferredPlaceOfCareFirstChoice: getSelectedPreferredPlaceOption(
        preferredPlaceOfCareOptions,
        endOfLifeCare.preferredPlaceOfCareFirstChoice
      ),

      preferredPlaceOfCareSecondChoice: getSelectedPreferredPlaceOption(
        preferredPlaceOfCareOptions,
        endOfLifeCare.preferredPlaceOfCareSecondChoice
      ),

      preferredPlaceOfDeathFirstChoice: getSelectedPreferredPlaceOption(
        preferredPlaceOfDeathOptions,
        endOfLifeCare.preferredPlaceOfDeathFirstChoice
      ),

      preferredPlaceOfDeathSecondChoice: getSelectedPreferredPlaceOption(
        preferredPlaceOfDeathOptions,
        endOfLifeCare.preferredPlaceOfDeathSecondChoice
      ),

      hasRespectFormCompleted: endOfLifeCare.hasRespectFormCompleted,

      respectFormFiles: endOfLifeCare.respectFormFiles,
      hasUniversalCarePlan: endOfLifeCare.hasUniversalCarePlan,
      isOnPalliativeCareRegister: endOfLifeCare.isOnPalliativeCareRegister,
      familyAwareOfPrognosis: endOfLifeCare.familyAwareOfPrognosis,
    };
    onSubmitThreeStepsData({ step4: payload }, position);
  };

  const uploadedRespectFormFileList = endOfLifeCare.respectFormFiles ?? [];

  const setUploadedRespectFormFileList: React.Dispatch<
    React.SetStateAction<FileListData[]>
  > = nextValue => {
    setEndOfLifeCare(prev => {
      const currentFiles = prev.respectFormFiles ?? [];

      const nextFiles =
        typeof nextValue === 'function'
          ? (nextValue as (prevState: FileListData[]) => FileListData[])(
              currentFiles
            )
          : nextValue;

      return {
        ...prev,
        respectFormFiles: nextFiles,
      };
    });
  };

  return (
    <div className="mt-5 md:mt-10">
      <h1 className="govuk-heading-xl">
        {residentDetails?.profile.full_name} - End of Life Care
      </h1>
      <SummaryCoreWishesSection
        summaryCoreWishes={endOfLifeCare.summaryCoreWishes}
        updateEndOfLifeCare={updateEndOfLifeCare}
      />
      <SaveAndContinueButton
        position={SuccessNotiPositionEnum.FIRST}
        showSuccessNotification={showSuccessNotification}
        onSave={handleSaveEndOfLifeCare}
      />

      <PreferredPlacesSection
        preferredPlaceOfCareFirstChoice={
          endOfLifeCare.preferredPlaceOfCareFirstChoice
        }
        preferredPlaceOfCareSecondChoice={
          endOfLifeCare.preferredPlaceOfCareSecondChoice
        }
        preferredPlaceOfDeathFirstChoice={
          endOfLifeCare.preferredPlaceOfDeathFirstChoice
        }
        preferredPlaceOfDeathSecondChoice={
          endOfLifeCare.preferredPlaceOfDeathSecondChoice
        }
        updateEndOfLifeCare={updateEndOfLifeCare}
      />
      <SaveAndContinueButton
        position={SuccessNotiPositionEnum.SECOND}
        showSuccessNotification={showSuccessNotification}
        onSave={handleSaveEndOfLifeCare}
      />
      <RespectFormSection
        userId={userId}
        hasRespectFormCompleted={endOfLifeCare.hasRespectFormCompleted}
        respectFormFiles={uploadedRespectFormFileList}
        setRespectFormFiles={setUploadedRespectFormFileList}
        updateEndOfLifeCare={updateEndOfLifeCare}
      />
      <SaveAndContinueButton
        position={SuccessNotiPositionEnum.THIRD}
        showSuccessNotification={showSuccessNotification}
        onSave={handleSaveEndOfLifeCare}
      />
      <PrognosisSection
        fullName={residentFullName}
        prognosisFromData={prognosisFromData}
        slug={slug}
        isUpdatedPronosis={isUpdatedPronosis}
        setIsUpdatedPrognosis={setIsUpdatedPrognosis}
      />
      <UniversalCarePlanSection
        hasUniversalCarePlan={endOfLifeCare.hasUniversalCarePlan}
        updateEndOfLifeCare={updateEndOfLifeCare}
      />
      <PalliativeCareRegisterSection
        residentFullName={residentDetails?.profile.full_name ?? ''}
        isOnPalliativeCareRegister={endOfLifeCare.isOnPalliativeCareRegister}
        familyAwareOfPrognosis={endOfLifeCare.familyAwareOfPrognosis}
        updateEndOfLifeCare={updateEndOfLifeCare}
      />

      <div className="flex items-center justify-between w-full mt-10 govuk-bp:mt-14 gap-5">
        <div className="govuk-button-group w-full justify-between">
          <button
            type="submit"
            disabled={callApiStatus.loading}
            className="govuk-button govuk-button--secondary"
            onClick={() => changeStep(StepChangeDirection.Previous)}
          >
            Previous
          </button>
          <div className="govuk-button-group w-full govuk-bp:w-fit">
            <button
              type="submit"
              className="govuk-button"
              data-module="govuk-button"
              disabled={callApiStatus.loading}
              onClick={e => {
                handleSaveEndOfLifeCare(e, SuccessNotiPositionEnum.LAST);
              }}
            >
              Save
            </button>
            <button
              type="submit"
              className="govuk-button"
              data-module="govuk-button"
              disabled={callApiStatus.loading}
              onClick={() => changeStep(StepChangeDirection.Next)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
      {callApiStatus.submitted === SubmitStatusEnum.SUCCESS && (
        <SuccessNotificationBanner
          title="This document has been saved."
          content={callApiStatus.message}
          duration={3000}
          noScrollToTop
        />
      )}
    </div>
  );
};

const StepFour: React.FC<StepFourProps> = props => {
  return (
    <ErrorsProvider>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <StepFourComponent {...props} />
    </ErrorsProvider>
  );
};

export default StepFour;
